import { createFileRoute } from '@tanstack/react-router';
import { FiCheckCircle, FiMapPin, FiShield, FiWatch } from 'react-icons/fi';
import { SeoLandingPage } from '@/components/marketing';
import devicesPng from '@/assets/devices.png';

function VisitationTrackerPage() {
  return (
    <SeoLandingPage
      path='/visitation-tracker'
      title='Visitation Tracker with GPS Check-Ins | Harbor'
      description='Track custody exchanges, on-time handoffs, and no-shows with GPS check-ins. Harbor keeps a visitation compliance record you can export — not just a calendar of whose weekend it is.'
      breadcrumbName='Visitation tracker'
      eyebrow='Exchanges & compliance'
      h1='Track visitation, exchanges, and on-time handoffs'
      hero='A visitation tracker is for what actually happened at the exchange — who arrived, who was late, and whether the handoff occurred. Harbor pairs location-verified check-ins with a record you can export later.'
      screenEvent='Visitation Tracker Screen'
      ctaLocation='Visitation Tracker'
      storeCampaign='visitation-tracker'
      featuresHeading='Document the exchange, not just the schedule'
      features={[
        {
          icon: FiMapPin,
          title: 'GPS exchange check-ins',
          description:
            'Check in when you arrive for a custody handoff. Harbor records a location-verified visit so “I was there” is not a text-message debate.',
          bullets: [
            'Location-verified custody handoffs',
            'On-time, late, and no-show tracking',
            'A compliance trail next to the calendar, not instead of it',
          ],
          image: devicesPng,
          imageAlt: 'Harbor on phone and laptop for visitation check-ins',
        },
        {
          icon: FiWatch,
          title: 'Late and no-show history',
          description:
            'Patterns matter more than a single missed minute. Harbor keeps exchange outcomes together so you can see whether lateness is occasional or chronic.',
          bullets: [
            'See exchange outcomes over weeks, not one argument',
            'Keep notes with the check-in, not in a private text thread',
            'Export the compliance record with your other reports',
          ],
        },
        {
          icon: FiCheckCircle,
          title: 'Works alongside the custody calendar',
          description:
            'The schedule answers “whose weekend is it?” The visitation tracker answers “did the exchange happen?” Harbor keeps both, so you are not stitching a calendar app to a notes app.',
          bullets: [
            'Calendar for the rotation, tracker for the handoff',
            'No second login for compliance logging',
            'Useful even if only one parent is documenting',
          ],
        },
        {
          icon: FiShield,
          title: 'A record you can hand to counsel',
          description:
            'When a lawyer or mediator asks what happened at exchanges, export the check-in history with messages and expenses instead of reconstructing it from screenshots.',
          bullets: [
            'Timestamped exchange outcomes',
            'Export with court-ready reports',
            'Keep documentation in Harbor, not across three apps',
          ],
        },
      ]}
      faqs={[
        {
          question: 'How is a visitation tracker different from a custody schedule app?',
          answer:
            'A schedule shows the planned rotation. A visitation tracker records whether the exchange happened, when someone arrived, and whether it was late or a no-show. Harbor does both.',
        },
        {
          question: 'Does Harbor use GPS for every parenting day?',
          answer:
            'Check-ins are for exchanges and handoffs, not constant location sharing. You record the pickup or drop-off, not a live map of the other parent.',
        },
        {
          question: 'Can I track visits if the other parent will not use Harbor?',
          answer:
            'Yes. Log your own arrivals and outcomes. You do not need both parents in the app to keep a personal compliance record.',
        },
        {
          question: 'Is this the same as court-ordered supervised visitation software?',
          answer:
            'No. Harbor is a co-parenting app with GPS exchange check-ins and exportable records. It is not a supervised-visitation monitoring service.',
        },
      ]}
      crossLinks={[
        {
          href: '/custody-schedule-app',
          label: 'Custody schedule app',
          description:
            'Set the repeating rotation and holidays first, then track whether exchanges follow the plan.',
        },
        {
          href: '/court-ready-records',
          label: 'Court-ready records',
          description:
            'Export check-ins with messages, expenses, and calendar history when you need a packet.',
        },
      ]}
    />
  );
}

export const Route = createFileRoute('/visitation-tracker')({
  component: VisitationTrackerPage,
});
